import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Customer } from '../types';

export default function CustomersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Partial<Customer> | null>(null);

  const { data: customers = [], isLoading } = useQuery<Customer[]>({
    queryKey: ['customers', search],
    queryFn: () => api.get('/customers', { params: { search: search || undefined } }).then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (c: Partial<Customer>) =>
      c.id ? api.put(`/customers/${c.id}`, c) : api.post('/customers', c),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });

  const startEdit = (c?: Customer) => {
    if (c) {
      setEditing({ ...c, address: c.address || { street: '', zip: '', city: '', country: '' } });
    } else {
      setEditing({ name: '', email: '', phone: '', taxId: '', notes: '', address: { street: '', zip: '', city: '', country: '' } });
    }
  };

  const addr = (c: Customer) => {
    const a = c.address;
    if (!a) return null;
    const parts = [a.street, [a.zip, a.city].filter(Boolean).join(' ')].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const mapsUrl = (c: Customer) => {
    const a = c.address;
    if (!a) return null;
    const q = [a.street, a.zip, a.city].filter(Boolean).join(', ');
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  };

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Kunden</h1>
          <p className="sp-page-subtitle">{customers.length} Kunden insgesamt</p>
        </div>
        <button onClick={() => startEdit()} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neuer Kunde
        </button>
      </div>

      {/* Search */}
      <div className="sp-search">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input placeholder="Kunden suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Edit form */}
      {editing && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">{editing.id ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Name *</label>
              <input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="sp-input" placeholder="Firmen- oder Kundenname" autoFocus />
            </div>
            <div>
              <label className="sp-label">E-Mail</label>
              <input type="email" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                className="sp-input" placeholder="kunde@email.de" />
            </div>
            <div>
              <label className="sp-label">Telefon</label>
              <input value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                className="sp-input" placeholder="+49 ..." />
            </div>
            <div>
              <label className="sp-label">USt-IdNr.</label>
              <input value={editing.taxId || ''} onChange={(e) => setEditing({ ...editing, taxId: e.target.value })}
                className="sp-input" placeholder="DE123456789" />
            </div>
          </div>

          {/* Adresse */}
          <div className="mt-5 pt-5 border-t border-surface-100">
            <h4 className="text-[13px] font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Adresse
            </h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="sp-label">Straße + Hausnummer</label>
                <input value={editing.address?.street || ''}
                  onChange={(e) => setEditing({ ...editing, address: { ...editing.address, street: e.target.value } })}
                  className="sp-input" placeholder="Musterstraße 123" />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="sp-label">PLZ</label>
                  <input value={editing.address?.zip || ''}
                    onChange={(e) => setEditing({ ...editing, address: { ...editing.address, zip: e.target.value } })}
                    className="sp-input" placeholder="12345" />
                </div>
                <div className="col-span-2">
                  <label className="sp-label">Ort</label>
                  <input value={editing.address?.city || ''}
                    onChange={(e) => setEditing({ ...editing, address: { ...editing.address, city: e.target.value } })}
                    className="sp-input" placeholder="Berlin" />
                </div>
                <div>
                  <label className="sp-label">Land</label>
                  <input value={editing.address?.country || ''}
                    onChange={(e) => setEditing({ ...editing, address: { ...editing.address, country: e.target.value } })}
                    className="sp-input" placeholder="Deutschland" />
                </div>
              </div>
            </div>
          </div>

          {/* Notizen */}
          <div className="mt-4">
            <label className="sp-label">Notizen</label>
            <textarea value={editing.notes || ''}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              className="sp-input min-h-[80px] resize-y" placeholder="Interne Notizen zum Kunden..." />
          </div>

          <div className="sp-form-actions">
            <button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending || !editing.name?.trim()} className="sp-btn-primary">
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setEditing(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kontakt</th>
              <th>Adresse</th>
              <th className="w-28 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4}>
                <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                  <svg className="sp-spinner" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Laden...
                </div>
              </td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={4}>
                <div className="sp-empty">
                  <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Keine Kunden gefunden</span>
                </div>
              </td></tr>
            ) : customers.map((c) => (
              <tr key={c.id} onClick={() => startEdit(c)} className="cursor-pointer hover:bg-brand-50/50 transition-colors">
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center text-[11px] font-bold text-brand-600 flex-shrink-0">
                      {c.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className="font-medium text-slate-800 block">{c.name}</span>
                      {c.taxId && <span className="text-[11px] text-slate-400">{c.taxId}</span>}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="space-y-0.5">
                    {c.email && (
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs">{c.email}</span>
                      </div>
                    )}
                    {c.phone && (
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-xs">{c.phone}</span>
                      </div>
                    )}
                    {!c.email && !c.phone && <span className="text-xs text-slate-300">-</span>}
                  </div>
                </td>
                <td>
                  {addr(c) ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{addr(c)}</span>
                      {mapsUrl(c) && (
                        <a href={mapsUrl(c)!} target="_blank" rel="noreferrer"
                          className="sp-btn-icon !w-7 !h-7 !text-brand-400 hover:!text-brand-600 hover:!bg-brand-50 flex-shrink-0"
                          title="In Google Maps öffnen">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">Keine Adresse</span>
                  )}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <button onClick={() => startEdit(c)} className="sp-btn-icon" title="Bearbeiten">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => { if (confirm('Kunde wirklich löschen?')) deleteMutation.mutate(c.id); }}
                      className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50" title="Löschen">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
